import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * `<chassis-frame>` — panel housing primitive.
 *
 * Provides the outer chassis as a positioning surface for child controls.
 * The frame owns the canonical viewBox supplied by the panel inventory;
 * children position themselves in viewBox units. The frame draws the
 * housing colour, the chamfered top edge that real broadcast panels have
 * cast into them, the recessed control wells around the cut-outs, and a
 * faint subsurface anodisation texture.
 *
 * The default slot accepts arbitrary children; the panel composition is
 * responsible for placing them with absolute positioning in percentages
 * of the chassis box (kept px-free per ADR-0009).
 */
@customElement('chassis-frame')
export class ChassisFrameElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: block;
        contain: layout paint;
        container-type: inline-size;
        inline-size: 100%;
        aspect-ratio: var(--chassis-aspect, 16 / 9);

        --cf-chassis: var(--chassis-colour, #1c1c1e);
        --cf-chassis-edge: var(--chassis-edge, color-mix(in oklab, var(--cf-chassis) 60%, black));
        --cf-chassis-bevel: var(--chassis-bevel, color-mix(in oklab, var(--cf-chassis) 80%, white));
        --cf-recess: var(--chassis-recess, color-mix(in oklab, var(--cf-chassis) 50%, black));
        --cf-accent: var(--chassis-accent, var(--cf-chassis-bevel));
      }

      .frame {
        position: relative;
        block-size: 100%;
        inline-size: 100%;
        border-radius: 0.55rem;
        background:
          radial-gradient(
            ellipse at 50% -20%,
            color-mix(in oklab, var(--cf-chassis-bevel) 40%, transparent 60%) 0%,
            transparent 60%
          ),
          linear-gradient(180deg, var(--cf-chassis-bevel) 0%, var(--cf-chassis) 8%, var(--cf-chassis) 92%, var(--cf-chassis-edge) 100%);
        box-shadow:
          inset 0 1px 0 color-mix(in oklab, var(--cf-chassis-bevel) 90%, white) / 0.18,
          inset 0 -2px 0 #000,
          0 1rem 2.4rem rgba(0, 0, 0, 0.55);
        overflow: hidden;
      }

      /* Subtle anodisation noise — replicated via tiled SVG mask for fidelity. */
      .frame::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image: radial-gradient(
            color-mix(in oklab, var(--cf-chassis-bevel) 18%, transparent 82%) 1px,
            transparent 1px
          );
        background-size: 0.45rem 0.45rem;
        mix-blend-mode: overlay;
        pointer-events: none;
        opacity: 0.4;
      }

      .slot-host {
        position: absolute;
        inset: 0;
        block-size: 100%;
        inline-size: 100%;
      }

      .nameplate {
        position: absolute;
        inset-block-end: 0.6rem;
        inset-inline-end: 1rem;
        font-family: var(--font-ui);
        font-size: 0.55rem;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: color-mix(in oklab, var(--cf-accent) 80%, white) / 0.55;
        opacity: 0.7;
        pointer-events: none;
        user-select: none;
      }
    `,
  ];

  /** Canonical viewBox in panel units. Kept for primitives that need to align in SVG. */
  @property({ type: String, attribute: 'view-box' }) accessor viewBox = '0 0 1000 600';

  /** Optional name plate text rendered in the bottom-right corner. */
  @property({ type: String }) accessor model = '';

  override render() {
    return html`
      <div class="frame" part="frame">
        <div class="slot-host"><slot></slot></div>
        ${this.model
          ? html`<div class="nameplate">${this.model}</div>`
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chassis-frame': ChassisFrameElement;
  }
}
